/* global fetch */

import { config } from '../config';
import { APIResponse, fetchAPI, headers, SpanTag, stleaf } from '../helpers';

export interface Map {
  id: string;
  name: string;
  createdBy?: string | null;
  createdAt: string;
  locationId?: string | null;
  location?: {
    id: string;
    name: string | null;
    streetAddress: string | null;
    suburb: string | null;
    state: string | null;
    country: string | null;
    center: [number, number] | null;
    bbox: [number, number, number, number] | null;
  } | null;
  mapCover: string | null;
  organisationName: string;
  creatorFname?: string | null;
  creatorLname?: string | null;
  folderId?: string | null;
  folderName?: string | null;
  fileCount?: number | null;
  totalGB?: number | null;
  measureSystem?: 'metric' | 'imperial';
  lengthUnit?: string | null;
  areaUnit?: string | null;
  volumeUnit?: string | null;
}

export interface GetManyMapsOptions {
  start?: number;
  limit?: number;
}

type GetMapsResponse = Promise<APIResponse<Map[]>>;
export const getMaps = (
  st: SpanTag,
  opts?: GetManyMapsOptions,
): GetMapsResponse => {
  const url = new URL(`${config.API_URL}/v1/maps/`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'getmps'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetMapsResponse;
};

type PostMapResponse = Promise<APIResponse<{ id: string }>>;
export type PostMapBody = {
  name?: string;
  organisationId?: string;
  /** Dashboard “create new map” flow; sent only from upload/process destination modal. */
  flow?: 'upload' | 'process';
};

export const postMap = (
  st: SpanTag,
  body?: PostMapBody,
): PostMapResponse => {
  const url = `${config.API_URL}/v1/maps/`;
  return fetchAPI(stleaf(st, 'pstmap'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    ...(body && Object.keys(body).length > 0
      ? { body: JSON.stringify(body) }
      : {}),
  }) as PostMapResponse;
};

/**
 * BEWARE, e2e these events need to broadcasted to all ws2 connected clients
 */
type UpdateMapResponse = Promise<APIResponse<Map>>;
export const updateMap = (
  st: SpanTag,
  id: string,
  body: Partial<Map>,
): UpdateMapResponse => {
  const url = `${config.API_URL}/v1/maps/${id}`;
  return fetchAPI(stleaf(st, 'updmap'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  }) as UpdateMapResponse;
};

export interface MapUnitSettings {
  measureSystem: 'metric' | 'imperial';
  lengthUnit: string | null;
  areaUnit: string | null;
  volumeUnit: string | null;
}
type GetMapUnitSettingsResponse = Promise<APIResponse<MapUnitSettings>>;
// Works for the authenticated owner and for public-share viewers (the fetch
// helper attaches the X-Auth-Token / X-Map-ID headers from the share link).
export const getMapUnitSettings = (
  st: SpanTag,
  mapId: string,
): GetMapUnitSettingsResponse => {
  const url = `${config.API_URL}/v1/maps/${mapId}/unit-settings`;
  return fetchAPI(stleaf(st, 'untget'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetMapUnitSettingsResponse;
};

type DeleteMapResponse = Promise<APIResponse<void>>;
export const deleteMap = (st: SpanTag, id: string): DeleteMapResponse => {
  const url = `${config.API_URL}/v1/maps/${id}`;
  return fetchAPI(stleaf(st, 'dltemp'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  }) as DeleteMapResponse;
};

type GetLayerMediaInfoResponse = Promise<
  APIResponse<{ thumbnailUrl?: string; fullImageUrl?: string; s3Key?: string }>
>;
export const getLayerMediaInfo = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  // 'inline' signs a PDF so it renders in-browser (Open in new tab) rather than
  // downloading. Omit for the default (download) behaviour.
  disposition?: 'inline' | 'attachment',
  // A drawing rendered as a raster: sign its PDF instead of the render.
  options?: { drawingSource?: boolean },
): GetLayerMediaInfoResponse => {
  const params = new URLSearchParams({
    ...(disposition && { disposition }),
    ...(options?.drawingSource && { drawing: 'source' }),
  }).toString();
  const query = params ? `?${params}` : '';
  const url = `${config.API_URL}/v1/maps/${mapId}/layers/${layerId}/media-info${query}`;
  return fetchAPI(stleaf(st, 'iosh9b'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** `url` is null unless the tileset is served through the gallery CDN. */
export const getLayerTilesetUrl = (
  st: SpanTag,
  mapId: string,
  layerId: string,
): Promise<APIResponse<{ url: string | null }>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/layers/${layerId}/tileset-url`;
  return fetchAPI(stleaf(st, 'tsurl1'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface LayerMediaSrid {
  srid: number;
  verticalDatum: string | null;
  geoidSeparation: number | null;
  geoidModel: string | null;
}

export const setLayerMediaSrid = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  srid: number,
): Promise<APIResponse<LayerMediaSrid>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/layers/${layerId}/media-srid`;
  return fetchAPI(stleaf(st, 'lmsrid'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ srid }),
  });
};

type GetOrganisationMapsResponse = Promise<APIResponse<Map[]>>;
export const getOrganisationMaps = (
  st: SpanTag,
  orgId: string,
  opts?: {
    start?: number;
    limit?: number;
    mapName?: string;
    startDate?: string;
    endDate?: string;
    getFiles?: string;
    locationBbox?: string;
    noCover?: string;
    folderId?: string;
    creatorScopeId?: string;
  },
): GetOrganisationMapsResponse => {
  const url = new URL(`${config.API_URL}/v1/maps/organisation/${orgId}`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'gorgmp'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetOrganisationMapsResponse;
};

type GetMapCoverResponse = Promise<APIResponse<{ mapCover: string | null }>>;
export const getMapCover = (
  st: SpanTag,
  mapId: string,
): GetMapCoverResponse => {
  const url = `${config.API_URL}/v1/maps/covers/${mapId}`;
  return fetchAPI(stleaf(st, 'gmpcvr'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetMapCoverResponse;
};

type GetMapFilesResponse = Promise<
  APIResponse<
    Record<
      string,
      {
        fileCount: number;
        totalGB: number;
      }
    >
  >
>;
export const getMapFiles = (
  st: SpanTag,
  mapIds: string[],
): GetMapFilesResponse => {
  const url = `${config.API_URL}/v1/maps/files`;
  return fetchAPI(stleaf(st, 'gmpfls'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mapIds }),
  }) as GetMapFilesResponse;
};

// Map Reports types
export interface MapReport {
  id: string;
  name: string;
  createdAt: string;
  mapId: string;
  mapName: string;
  s3Key: string | null;
  fileExtension: string | null;
  creatorName: string | null;
  reportType:
    | 'map_canvas_export'
    | 'image_report'
    | 'annotation_report'
    | 'processing_report'
    | 'custom_report';
}

type GetOrganisationReportsResponse = Promise<APIResponse<MapReport[]>>;
export const getOrganisationReports = (
  st: SpanTag,
  orgId: string,
  opts?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  },
): GetOrganisationReportsResponse => {
  const url = new URL(`${config.API_URL}/v1/maps/reports/${orgId}`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'gorgrp'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetOrganisationReportsResponse;
};

export interface ProcessingReport {
  id: string;
  name: string;
  createdAt: string;
  mapId: string;
  mapName: string;
  s3Key: string | null;
  fileExtension: string | null;
  creatorName: string | null;
  status: 'Generating' | 'Ready';
}

type GetProcessingReportsResponse = Promise<APIResponse<ProcessingReport[]>>;
export const getProcessingReports = (
  st: SpanTag,
  orgId: string,
  opts?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  },
): GetProcessingReportsResponse => {
  const url = new URL(`${config.API_URL}/v1/maps/processing-reports/${orgId}`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'gprcrp'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetProcessingReportsResponse;
};

export interface ProcessingOverview {
  id: string;
  name: string;
  createdAt: string;
  mapId: string;
  mapName: string;
  imagesCount?: number;
  groundResolution?: string;
  flightAltitude?: string;
  coverageArea?: string;
  reprojectionError?: string;
  previewUrl?: string | null;
}

type GetProcessingOverviewResponse = Promise<APIResponse<ProcessingOverview[]>>;
export const getProcessingOverview = (
  st: SpanTag,
  orgId: string,
  opts?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  },
): GetProcessingOverviewResponse => {
  const url = new URL(`${config.API_URL}/v1/maps/processing-overview/${orgId}`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'gprovw'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetProcessingOverviewResponse;
};

export interface MostViewedMapCollaborator {
  fname: string;
  lname: string;
  profilePic: string | null;
}

export interface MostViewedMap {
  mapId: string;
  mapName: string;
  viewCount: number;
  updatedAt: string | null;
  commentCount: number;
  collaboratorCount: number;
  collaborators: MostViewedMapCollaborator[];
}

type GetMostViewedMapsResponse = Promise<APIResponse<MostViewedMap[]>>;
export const getMostViewedMaps = (
  st: SpanTag,
  workspaceId: string,
  opts?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  },
): GetMostViewedMapsResponse => {
  const url = new URL(
    `${config.API_URL}/v1/maps/most-viewed/${workspaceId}`,
  );
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'loh6tu'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetMostViewedMapsResponse;
};

export interface RecentlySharedMapCollaborator {
  fname: string;
  lname: string;
  profilePic: string | null;
}

export interface RecentlySharedMap {
  mapId: string;
  mapName: string;
  sharedAt: string;
  shareCount: number;
  sharerFname: string;
  sharerLname: string;
  sharerProfilePic: string | null;
  collaboratorCount: number;
  collaborators: RecentlySharedMapCollaborator[];
  /** Active share link URL for Copy link (null if map has no active share link) */
  shareUrl: string | null;
}

type GetRecentlySharedMapsResponse = Promise<APIResponse<RecentlySharedMap[]>>;
export const getRecentlySharedMaps = (
  st: SpanTag,
  workspaceId: string,
  opts?: { limit?: number },
): GetRecentlySharedMapsResponse => {
  const url = new URL(
    `${config.API_URL}/v1/maps/recently-shared/${workspaceId}`,
  );
  if (opts?.limit != null) url.searchParams.set('limit', String(opts.limit));
  return fetchAPI(stleaf(st, 'nai6po'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetRecentlySharedMapsResponse;
};

export const getMapLocation = (
  st: SpanTag,
  mapId: string,
): Promise<
  APIResponse<{
    center: [number, number];
    boundingBox: [number, number, number, number];
  }>
> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/location`;
  return fetchAPI(stleaf(st, 'moo8wl'), url, {
    method: 'GET',
    credentials: 'include',
    headers: headers,
  });
};

export const getMapLocationInfo = (
  st: SpanTag,
  mapId: string,
): Promise<
  APIResponse<{
    locationId: string | null;
    location: {
      id: string;
      name: string | null;
      streetAddress: string | null;
      suburb: string | null;
      state: string | null;
      country: string | null;
      center: [number, number] | null;
      bbox: [number, number, number, number] | null;
    } | null;
    /** First `multiband_cog` (else `singleband_cog`) bbox [w,s,e,n], or null */
    cogBoundingBox: [number, number, number, number] | null;
  }>
> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/location-info`;
  return fetchAPI(stleaf(st, 'shae9e'), url, {
    method: 'GET',
    credentials: 'include',
    headers: headers,
  });
};

export interface AvailableDem {
  mediaId: string;
  title: string;
  /** When the media row was written — upload/processing time, not a survey date. */
  createdAt: string | null;
  /**
   * Real survey date, traced to the source imagery's EXIF capture time. Null
   * for DEMs uploaded directly to Birdi, which have no processing job behind
   * them and therefore no recorded capture date.
   */
  captureDate: string | null;
  /** What to sort and display by: `captureDate` where known, else `createdAt`. */
  effectiveDate: string;
  /** Already a layer on the map being worked in — these lead the picker. */
  inCurrentMap: boolean;
  bbox: [number, number, number, number] | null;
  baseType: string;
}

/**
 * DEMs (DTM/DSM) across the map's workspace that sit at this map's location,
 * for the difference-grid "compared against" picker. Filtered server-side by
 * bbox overlap with the comparison surface (`excludeMediaId`). Any single-band
 * surface qualifies, including previous difference grids.
 */
export const getAvailableDems = (
  st: SpanTag,
  mapId: string,
  params: { excludeMediaId?: string },
): Promise<
  APIResponse<{ items: AvailableDem[]; excludedCaptureDate: string | null }>
> => {
  const url = new URL(`${config.API_URL}/v1/maps/${mapId}/available-dems`);
  if (params.excludeMediaId) {
    url.searchParams.set('excludeMediaId', params.excludeMediaId);
  }
  return fetchAPI(stleaf(st, 'avdem1'), url, {
    method: 'GET',
    credentials: 'include',
    headers: headers,
  });
};

export const updateMapLocation = (
  st: SpanTag,
  mapId: string,
  location: {
    name?: string | null;
    streetAddress?: string | null;
    suburb?: string | null;
    state?: string | null;
    country?: string | null;
    center: [number, number];
    bbox?: [number, number, number, number] | null;
  } | null,
): Promise<
  APIResponse<{
    locationId: string | null;
    location: {
      id: string;
      name: string | null;
      streetAddress: string | null;
      suburb: string | null;
      state: string | null;
      country: string | null;
      center: [number, number] | null;
      bbox: [number, number, number, number] | null;
    } | null;
  }>
> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/location`;
  return fetchAPI(stleaf(st, 'dooy3s'), url, {
    method: 'PUT',
    credentials: 'include',
    headers: headers,
    body: JSON.stringify({ location }),
  });
};

export const generateMapShareLink = (
  st: SpanTag,
  mapId: string,
  expiryDays: number,
): Promise<APIResponse<{ signedShareUrl: string }>> => {
  const url = `${config.API_URL}/v1/maps/create-share-link`;
  return fetchAPI(stleaf(st, 'gmapsh'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mapId }),
  });
};

export const getMapShareLink = (
  st: SpanTag,
  mapId: string,
): Promise<
  APIResponse<{ signedShareUrl: string; canCreateShareLink: boolean }>
> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/active-share-link`;
  return fetchAPI(stleaf(st, 'gmapsh'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const deleteMapShareLink = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<{ signedShareUrl: string }>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/share-link`;
  return fetchAPI(stleaf(st, 'l40em2'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const patchMapShareCount = (
  st: SpanTag,
  shareId: string,
): Promise<APIResponse<{ signedShareUrl: string }>> => {
  const url = `${config.API_URL}/v1/maps/${shareId}/share-link-viewed`;
  return fetchAPI(stleaf(st, 'a7b3c9'), url, {
    method: 'PATCH',
    credentials: 'include',
    headers,
  });
};

export const getMapLayerLocation = (
  st: SpanTag,
  mapId: string,
  layerId: string,
): Promise<
  APIResponse<{
    center: [number, number];
    boundingBox: [number, number, number, number];
  }>
> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/layers/${layerId}/location`;
  return fetchAPI(stleaf(st, 'soo8wl'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface MediaLayerExportRequest {
  folderId: string;
  folderName: string;
}

export interface MediaLayerSelectionRequest {
  layerIds: string[];
}

export interface MediaLayerExportResponse {
  message: string;
  jobId: string;
  mediaLayerCount: number;
  loadingLayerId: string;
  folderName?: string;
}

export const mediaPDFGeneration = (
  st: SpanTag,
  mapId: string,
  request: MediaLayerExportRequest,
): Promise<APIResponse<MediaLayerExportResponse>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/pdf-generation-media`;
  return fetchAPI(stleaf(st, 'media-export'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const mediaPDFGenerationSelection = (
  st: SpanTag,
  mapId: string,
  request: MediaLayerSelectionRequest,
): Promise<APIResponse<MediaLayerExportResponse>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/pdf-generation-media-selection`;
  return fetchAPI(stleaf(st, 'media-export-selection'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export interface AnnotationReportUploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
}

/**
 * Requests a presigned PUT URL the client uses to upload a captured map image
 * directly to S3 (avoids the API JSON body-size limit). Returns the s3 key to
 * pass to `annotationPDFGenerationSelection`.
 */
export const annotationReportUploadUrl = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<AnnotationReportUploadUrlResponse>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/annotation-report/upload-url`;
  return fetchAPI(stleaf(st, 'annotation-report-upload-url'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({}),
  });
};

export interface AnnotationSelectionRequest {
  layerIds: string[];
  mapImageS3Key: string;
  /** Actual geographic extent of the captured image (for thumbnail cropping). */
  mapImageBounds?: {
    west: number;
    south: number;
    east: number;
    north: number;
  };
  mapImageWidth?: number;
  mapImageHeight?: number;
  /**
   * Per-annotation isolated thumbnails (only that annotation drawn), uploaded to
   * S3. When present the report embeds these instead of cropping the overview.
   */
  thumbnails?: { layerId: string; s3Key: string }[];
}

export const annotationPDFGenerationSelection = (
  st: SpanTag,
  mapId: string,
  request: AnnotationSelectionRequest,
): Promise<APIResponse<MediaLayerExportResponse>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/pdf-generation-annotation-selection`;
  return fetchAPI(stleaf(st, 'annotation-export-selection'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export interface DuplicateMapOptions {
  name?: string;
  duplicateLayers?: boolean;
  organisationId?: string;
}

type DuplicateMapResponse = Promise<APIResponse<Map>>;
export const duplicateMap = (
  st: SpanTag,
  mapId: string,
  options?: DuplicateMapOptions,
): DuplicateMapResponse => {
  const url = `${config.API_URL}/v1/maps/${mapId}/duplicate`;
  return fetchAPI(stleaf(st, 'dupmap'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(options || {}),
  }) as DuplicateMapResponse;
};

// Table View Preset Types and Endpoints
export interface TableColumnState {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  order: number;
}

export interface TableColumnFilter {
  columnId: string;
  filterType: string;
  filterValue: any;
}

export interface TableViewPreset {
  id: string;
  name: string;
  mapId: string;
  columnStates: TableColumnState[];
  columnFilters: TableColumnFilter[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTablePresetRequest {
  name: string;
  columnStates: TableColumnState[];
  columnFilters: TableColumnFilter[];
}

export interface UpdateTablePresetRequest {
  name?: string;
  columnStates?: TableColumnState[];
  columnFilters?: TableColumnFilter[];
}

// Get all presets for a map
export const getMapTablePresets = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<TableViewPreset[]>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/table-presets`;
  return fetchAPI(stleaf(st, 'gtblpre'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

// Create a new table preset
export const createMapTablePreset = (
  st: SpanTag,
  mapId: string,
  request: CreateTablePresetRequest,
): Promise<APIResponse<TableViewPreset>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/table-presets`;
  return fetchAPI(stleaf(st, 'ctblpre'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

// Update an existing table preset
export const updateMapTablePreset = (
  st: SpanTag,
  mapId: string,
  presetId: string,
  request: UpdateTablePresetRequest,
): Promise<APIResponse<TableViewPreset>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/table-presets/${presetId}`;
  return fetchAPI(stleaf(st, 'utblpre'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export interface MapSearchLayer {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
  data?: Record<string, unknown> | null;
}

export interface MapSearchResult {
  id: string;
  name: string;
  organisationId: string;
  createdAt: string;
  creator: {
    userId: string | null;
    fname: string | null;
    lname: string | null;
  };
  location: {
    id: string;
    name: string | null;
    streetAddress: string | null;
    suburb: string | null;
    state: string | null;
    country: string | null;
    center: [number, number] | null;
    bbox: [number, number, number, number] | null;
  } | null;
  fileCount: number;
  totalGB: number;
  thumbnail: string | null;
}

export interface SearchMapsResponse {
  items: MapSearchResult[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SearchMapsOptions {
  search?: string;
  createdBy?: string;
  organisationId?: string;
  locationBbox?: string;
  excludeMapId?: string;
  page?: number;
  pageSize?: number;
}

export const searchMaps = (
  st: SpanTag,
  opts?: SearchMapsOptions,
): Promise<APIResponse<SearchMapsResponse>> => {
  const url = new URL(`${config.API_URL}/v1/maps/search`);
  if (opts) {
    const filteredOpts = Object.fromEntries(
      Object.entries(opts).filter(([_, value]) => value !== undefined),
    );
    url.search = new URLSearchParams(filteredOpts as any).toString();
  }
  return fetchAPI(stleaf(st, 'srchmps'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as Promise<APIResponse<SearchMapsResponse>>;
};

export interface ImportMapLayersBody {
  sourceMapId: string;
  groups: Array<{ name: string; layerIds: string[] }>;
}

export const importMapLayers = (
  st: SpanTag,
  targetMapId: string,
  body: ImportMapLayersBody,
): Promise<APIResponse<unknown>> => {
  const url = `${config.API_URL}/v1/maps/${targetMapId}/layers/import`;
  return fetchAPI(stleaf(st, 'impmlyr'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
};

// Delete a table preset
export const deleteMapTablePreset = (
  st: SpanTag,
  mapId: string,
  presetId: string,
): Promise<APIResponse<void>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/table-presets/${presetId}`;
  return fetchAPI(stleaf(st, 'dtblpre'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const getMapSearchLayers = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<{ layers: MapSearchLayer[] }>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/search-layers`;
  return fetchAPI(stleaf(st, 'loh0ae'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface ProcessingEligibleFolderItem {
  id: string;
  pathLabel: string;
}

export const getProcessingEligibleFolders = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<{ items: ProcessingEligibleFolderItem[] }>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/processing-eligible-folders`;
  return fetchAPI(stleaf(st, 'phae8g'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface MapVectorTileSignedAccessLayer {
  tilesUrlTemplate: string;
  tile_sig: string;
  audience_b64: string;
  tile_exp: string;
  mapId: string;
  vectorId: string;
  mapFeaturesUrls: {
    features: string;
    count: string;
    fields: string;
    fieldCategoriesTemplate: string;
  };
}

export interface MapVectorTileSignedAccessResponse {
  enabled: boolean;
  layers: Record<string, MapVectorTileSignedAccessLayer>;
}

export const getMapVectorTileSignedAccess = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<MapVectorTileSignedAccessResponse>> => {
  const url = `${config.API_URL}/v1/maps/${mapId}/layers/vector-tile-signed-access`;
  return fetchAPI(stleaf(st, 'vtsacc'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
    // Minted URLs and tile_sig change over time; conditional GET → 304 is not
    // OK here — parseAPIResponse treats !ok as failure and 304 has no JSON body.
    cache: 'no-store',
  }) as Promise<APIResponse<MapVectorTileSignedAccessResponse>>;
};

export interface DrawingPageRequest {
  page: number;
  name: string;
  georeferenced: boolean;
}

/** Add as map overlay: one layer per page, on the source layer's file, beside it. */
export const addDrawingFromLayer = (
  st: SpanTag,
  mapId: string,
  body: { sourceLayerId: string; pages: DrawingPageRequest[] },
): Promise<APIResponse<{ layers: { id: string; data: unknown }[] }>> =>
  fetchAPI(stleaf(st, 'dr4wpg'), `${config.API_URL}/v1/maps/${mapId}/drawings`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });

export interface DrawingAlignmentRequest {
  method: 'similarity' | 'affine' | 'leastSquares';
  points: { drawing: [number, number]; map: [number, number]; label?: string }[];
}

/** Saves the control points; the API refits them and renders the page. */
export const alignDrawing = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  body: DrawingAlignmentRequest,
): Promise<APIResponse<{ fit: { residualsM: number[]; rmsM: number; epsg: number } }>> =>
  fetchAPI(
    stleaf(st, 'dr4wal'),
    `${config.API_URL}/v1/maps/${mapId}/drawings/${layerId}/alignment`,
    {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    },
  );
